import { JSX, useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Typography,
} from "@mui/material";

import { getTools, Tool } from "../../utils/billingUtil";
import { sx } from "./styles";
import heroBg from "../../assets/images/nrtb-hero.png";
import {SpaceBar} from "@mui/icons-material";
import theme from "../../theme";
import {useAuth} from "../../auth/AuthContext";
import {useNavigate} from "react-router-dom";

const FEATURED_TOOL_CODES: string[] = [
    "OPTIMIZATION_INGEST_SCHEDULER",
    "TRACE_TOGGLE",
    "SYNTHETICS_SYNTHESIZER",
    "FORENSIC_FINGERPRINT",
    "SYNTHETICS_VINDICATOR",
    "USER_OPTIMIZER",
    "REMEDIATOR",
    "SERVICE_LAYER_ANALYZER",
    "INFRASTRUCTURE_FOUR_FREE",
    "TRANSACTION_TRACKER",
];

export default function PublicWelcome(): JSX.Element {
    const {isAuthenticated, account, login, logout} = useAuth();
    const navigate = useNavigate();
    const [tools, setTools] = useState<Tool[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    // ==================================================
    // GET THE LIST OF TOOLS FROM THE API
    // ==================================================

    useEffect(() => {
        let mounted = true;

        (async () => {
            try {

                const { tools } = await getTools();

                if (!mounted) return;

                const featured = FEATURED_TOOL_CODES
                    .map(code => tools.find(t => t.code === code))
                    .filter((t): t is Tool => t !== undefined);

                setTools(featured);
            } catch (e) {
                console.error("Error loading tools for landing page", e);
                if (mounted) {
                    // Safe fallback: empty list rather than hard fail
                    setTools([]);
                    setError("Unable to load tools at this time.");
                }
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const isLoading = tools === null;

    /**
     * When a user clicks on "Subscribe" on one of the tools, prompt them to login if they are not already.
     * Then, direct them to the marketplace page.
     */
    const onClickSubscribe = async () => {
        if (!isAuthenticated){
            await login();
        }

        navigate("/managesubscriptions", {replace: true});
    }

    return (
        <Box sx={sx.root}>
            {/* Hero Section */}
            <Box sx={sx.heroSection(heroBg)}>
                <Box sx={sx.heroTextWrapper}>
                    <Typography variant="h3" fontWeight="bold">
                        WELCOME <br/> TO&nbsp;
                        <Typography variant="h3" fontWeight="bold" component="span" color="primary">
                            NEW RELIC <br/> TOOLBOX
                        </Typography>
                    </Typography>

                    <Typography variant="body1">
                        Take charge of your New Relic environment with the industry's first
                        purpose-built administrator toolkit. Manage ingest, users, alerts, and
                        observability fidelity from one intuitive control center.
                    </Typography>
                </Box>
            </Box>

            {/* Tools Section */}
            <Box>
                <Box sx={sx.toolsInner}>
                    <Typography variant="h4" mt={3} mb={3}>
                        DESIGNED FOR&nbsp;
                        <Typography component="span" variant="h4" fontWeight="bold" color="primary">
                            ADMINISTRATORS.
                        </Typography>
                        <br/>BUILT FOR&nbsp;
                        <Typography component="span" variant="h4" fontWeight="bold" color="primary">
                            IMPACT.
                        </Typography>
                    </Typography>

                    <Typography variant="body1">
                        The New Relic Toolbox is a suite of precision-engineered tools
                        developed to simplify, optimize, and enhance how enterprises manage
                        their observability environments. From reducing ingest costs to
                        improving monitoring accuracy, each tool is designed to solve a real
                        operational challenge faced by today&apos;s platform teams.
                        Together, they form a unified framework that drives efficiency,
                        governance, and performance visibility across your observability
                        stack.
                    </Typography>

                    {isLoading ? (
                        <Box sx={sx.loaderRow}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {error && (
                                <Typography variant="body2" sx={sx.errorText}>
                                    {error}
                                </Typography>
                            )}

                            <Grid container spacing={2} sx={sx.toolGrid}>
                                {tools?.map((t) => (
                                    <Grid item xs={12} md={6} key={t.code}>
                                        <Box sx={sx.toolCard(theme, t.isReleased)} onClick={() => onClickSubscribe()}>
                                            <Box
                                                component="img"
                                                src={`/tool_icons/${t.code}.png`}
                                                alt={`${t.name} icon`}
                                                onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/tool_icons/NRTB.png"; }}
                                                sx={sx.toolIcon}
                                            />

                                            <Box sx={sx.toolCardContent}>
                                                <Typography variant="h6" sx={sx.toolTitle}>
                                                    {t.name}
                                                </Typography>
                                                <Typography variant="body1" color="primary" mt={0}>
                                                    {t.isReleased ? "OUT NOW" : `COMING SOON: ${t.releaseDate}`}
                                                </Typography>
                                                <Typography variant="body2" sx={sx.toolBlurb}>
                                                    {t.blurb}
                                                </Typography>
                                            </Box>

                                            <Box sx={sx.toolSubscribeRail}>
                                                <Typography variant="button" sx={sx.toolSubscribeText}>
                                                    Subscribe
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
